/**
 * Purpose: Tiny wrappers around the browser Web Speech API used by the
 *          copilot widget when `voice="browser"`. Exposes one hook for
 *          speech-to-text (push-to-talk style) and one helper for
 *          speech synthesis. Falls back gracefully when the API is
 *          unavailable (Firefox without flags, very old Safari, etc.) —
 *          callers see `supported: false` and hide the mic button.
 *
 * Implementation notes:
 *   - `voice="llm"` is NOT implemented here. That mode would route audio
 *     through the LLM service's STT/TTS endpoints; reserved as a future
 *     path. Keeping the prop union in place lets a host opt in later
 *     without an API churn.
 *   - SpeechRecognition is a vendor-prefixed global on most browsers.
 *     We accept either the unprefixed or the `webkit` form. Voices for
 *     synthesis load asynchronously; we trust the browser default.
 *   - Recognition runs in `interimResults: true` mode so the user sees
 *     typing-style feedback while they speak. The host decides what to
 *     do with the final transcript (the widget dictates it into the
 *     composer, or submits it in voice-conversation mode).
 *   - `listening` is mirrored in a ref so `start()` stays correct when it
 *     is called from a long-lived closure (e.g. a speech-synthesis `onend`
 *     callback re-opening the mic in voice-conversation mode).
 *   - `speak()` splits long text into sentence-sized utterances. Chrome's
 *     network voices silently stop after ~15 s of continuous speech; short
 *     queued utterances avoid that. Utterances are kept in a module-level
 *     array until they finish because Chrome can garbage-collect a pending
 *     utterance and never fire its `end` event.
 *
 * Created: 2026-05-09 18:00
 *
 * Changelog:
 *   - 2026-05-09 18:00 — Initial version. P6 of the copilot rollout.
 *   - 2026-09-25 08:01 — Dictation + voice-conversation support: STT gains
 *                        `onNoResult` (ended without speech), ignores our
 *                        own `aborted` error, and tracks `listening` in a
 *                        ref; `speak()` gains an `onEnd` callback, sentence
 *                        chunking (lines without closing punctuation
 *                        get a full stop so list items are read with a
 *                        pause), and GC-safe utterance retention; add
 *                        `abort()` to discard an utterance. Why: the widget's
 *                        mic now dictates into the composer and a separate
 *                        hands-free mode re-opens the mic after each reply.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
/**
 * Vendor-detection. Web Speech is not on every browser; we hide the
 * mic UI rather than throw when it is missing.
 */
export function speechRecognitionSupported() {
    if (typeof window === 'undefined')
        return false;
    return Boolean(window.SpeechRecognition ??
        window.webkitSpeechRecognition);
}
export function speechSynthesisSupported() {
    return typeof window !== 'undefined' && 'speechSynthesis' in window;
}
function getSRCtor() {
    if (typeof window === 'undefined')
        return null;
    const w = window;
    return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}
/**
 * Push-to-talk hook. Call `start()` to begin listening, `stop()` to
 * end early. Recognition auto-stops on user silence; `onFinal` fires
 * with the consolidated transcript either way.
 */
export function useSpeechToText(opts) {
    const [listening, setListening] = useState(false);
    const listeningRef = useRef(false);
    const recRef = useRef(null);
    const supported = speechRecognitionSupported();
    // Keep the latest callbacks in a ref so the recognition handler
    // doesn't see a stale closure when the host re-renders.
    const cbsRef = useRef(opts);
    cbsRef.current = opts;
    const setListeningBoth = useCallback((v) => {
        listeningRef.current = v;
        setListening(v);
    }, []);
    const stop = useCallback(() => {
        const rec = recRef.current;
        if (!rec)
            return;
        try {
            rec.stop();
        }
        catch {
            // Some browsers throw if stop() is called before start; ignore.
        }
    }, []);
    const abort = useCallback(() => {
        const rec = recRef.current;
        if (!rec)
            return;
        // Detach handlers first so no late onresult/onend fires into the host.
        rec.onresult = null;
        rec.onerror = null;
        rec.onend = null;
        try {
            rec.abort();
        }
        catch {
            // ignore
        }
        recRef.current = null;
        setListeningBoth(false);
    }, [setListeningBoth]);
    const start = useCallback(() => {
        if (!supported || listeningRef.current)
            return;
        const Ctor = getSRCtor();
        if (!Ctor)
            return;
        const rec = new Ctor();
        rec.lang =
            cbsRef.current.lang ?? (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
        rec.interimResults = true;
        rec.continuous = false;
        let finalText = '';
        let errored = false;
        rec.onresult = (ev) => {
            let interim = '';
            finalText = '';
            for (let i = 0; i < ev.results.length; i += 1) {
                const r = ev.results[i];
                const piece = r[0].transcript;
                if (r.isFinal)
                    finalText += piece;
                else
                    interim += piece;
            }
            const shown = `${finalText}${interim}`.trim();
            if (shown)
                cbsRef.current.onInterim?.(shown);
        };
        rec.onerror = (ev) => {
            if (ev.error === 'aborted')
                return;
            errored = true;
            cbsRef.current.onError?.(ev.error);
        };
        rec.onend = () => {
            if (recRef.current === rec)
                recRef.current = null;
            setListeningBoth(false);
            const text = finalText.trim();
            if (text)
                cbsRef.current.onFinal(text);
            else if (!errored)
                cbsRef.current.onNoResult?.();
        };
        recRef.current = rec;
        setListeningBoth(true);
        try {
            rec.start();
        }
        catch (err) {
            recRef.current = null;
            setListeningBoth(false);
            cbsRef.current.onError?.(err instanceof Error ? err.message : String(err));
        }
    }, [supported, setListeningBoth]);
    useEffect(() => () => {
        // Tear down on unmount.
        const rec = recRef.current;
        if (rec) {
            rec.onresult = null;
            rec.onerror = null;
            rec.onend = null;
            try {
                rec.abort();
            }
            catch {
                // ignore
            }
        }
    }, []);
    return { supported, listening, start, stop, abort };
}
// Utterances currently queued. Holding a reference prevents Chrome from
// garbage-collecting them before `end` fires (a long-standing bug).
const liveUtterances = [];
// Bumped on every speak()/stopSpeaking() so callbacks from a cancelled
// batch can tell they are stale and must not fire `onEnd`.
let speakGeneration = 0;
/** Split text into utterance-sized chunks at sentence / line boundaries. */
function chunkForSpeech(text, maxLen = 220) {
    // No regex lookbehind: it is a parse-time SyntaxError on Safari < 16.4,
    // which would take the whole CDN bundle down with it.
    const sentences = text
        .replace(/([.!?…;:])\s+/g, '$1\n')
        .split(/\n+/)
        .map((s) => s.trim())
        .filter(Boolean)
        // Lines without closing punctuation (list items, headings) get a full
        // stop so the voice pauses between them instead of running them on.
        .map((s) => (/[.!?…;:,]$/.test(s) ? s : `${s}.`));
    const chunks = [];
    let current = '';
    for (const sentence of sentences) {
        if (!current)
            current = sentence;
        else if (current.length + 1 + sentence.length <= maxLen)
            current += ` ${sentence}`;
        else {
            chunks.push(current);
            current = sentence;
        }
    }
    if (current)
        chunks.push(current);
    return chunks;
}
/**
 * Speak `text` via the browser's SpeechSynthesis. Best-effort — silent
 * no-op if synthesis is unavailable or `text` is empty. Cancels any
 * ongoing speech first so back-to-back replies don't queue up.
 * `onEnd` fires once after the last chunk finishes (or fails), unless a
 * later speak()/stopSpeaking() superseded this call.
 */
export function speak(text, lang, onEnd) {
    if (!speechSynthesisSupported()) {
        onEnd?.();
        return;
    }
    const chunks = chunkForSpeech(text);
    const synth = window.speechSynthesis;
    synth.cancel();
    liveUtterances.length = 0;
    const generation = ++speakGeneration;
    if (chunks.length === 0) {
        onEnd?.();
        return;
    }
    chunks.forEach((chunk, i) => {
        const utter = new SpeechSynthesisUtterance(chunk);
        if (lang)
            utter.lang = lang;
        const isLast = i === chunks.length - 1;
        const finish = () => {
            const idx = liveUtterances.indexOf(utter);
            if (idx >= 0)
                liveUtterances.splice(idx, 1);
            if (isLast && generation === speakGeneration)
                onEnd?.();
        };
        utter.onend = finish;
        utter.onerror = finish;
        liveUtterances.push(utter);
        synth.speak(utter);
    });
}
/** Stop any in-flight speech synthesis (and suppress pending `onEnd`s). */
export function stopSpeaking() {
    speakGeneration += 1;
    liveUtterances.length = 0;
    if (!speechSynthesisSupported())
        return;
    window.speechSynthesis.cancel();
}
//# sourceMappingURL=voice.js.map